import React, {useState, useEffect} from 'react';

import {Button} from './components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './components/ui/select';
import {Spinner} from './components/ui/spinner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './components/ui/dialog';
import {Separator} from './components/ui/separator';
import {Edit} from 'lucide-react';

type NestedStates = readonly (readonly (readonly string[])[])[];

/**
 * Props for the StateEditor component.
 */
export type StateEditorProps = {
  current: string | null;
  states: NestedStates;
  onUpdate(newState: string): void;
  loading: boolean;
  error: Error | undefined;
};

/**
 * Get options to display in the combo boxes.
 */
const gatherCombos = (
  states: NestedStates,
  stateParts: string[],
): string[][] => {
  const combos: string[][] = [[]];

  for (const row of states) {
    // Every first-combo option is always visible.
    for (let j = 0; j < row.length; j++) {
      if (j === 0 || row[j - 1].includes(stateParts[j - 1])) {
        for (const opt of row[j]) {
          combos[j] = combos[j] || [];
          combos[j].push(opt);
        }
      }
    }
  }

  return combos;
};

/**
 * Serialize a state slug.
 */
const ser = (stateParts: string[]) => stateParts.join('|');

/**
 * Deserialize a state slug.
 */
const des = (state: string | null) => (state ? state.split('|') : []);

/**
 * Modal-based combo box for setting the client's FSM state.
 *
 * The client is sometimes in multiple states at once, depending on which
 * machine they are in and where. For example, a user might start in a single
 * state, then enter two parallel machines in which they are in two states
 * simultaneously, and then return to a single state.
 *
 * States are serialized with `|`, so for example if the client is in state
 * `a` and `b` simultaneously, the state returned by the server is `a|b`.
 * For display in the UI, we use a variable number of combo boxes. The first
 * box will contain options for all of the top-level states. Additional combo
 * boxes will be rendered only when the client is eligible to be in parallel
 * states. Note that when a client is _eligible_ to be in parallel states,
 * they _must_ have one state value for each parallel machine; they are not
 * optional states.
 *
 * The `onUpdate` method gets passed a serialized parallel state slug whenever
 * a combo box is updated.
 */
export const StateEditor = ({
  states,
  current,
  onUpdate,
  error,
  loading,
}: StateEditorProps) => {
  const [open, setOpen] = useState(false);
  const [stateParts, setStateParts] = useState(des(current));
  const combos = gatherCombos(states, stateParts);

  useEffect(() => {
    setStateParts(des(current));
  }, [current]);

  const update = (nestedStates: NestedStates, value: string, col: number) => {
    const newIdx = nestedStates.findIndex((r) => r[col]?.includes(value));
    const newRow = nestedStates[newIdx];
    const newState = [...stateParts].slice(0, newRow.length);
    newState[col] = value;
    newState.slice(0, newState.length);
    for (let i = 0; i < newState.length; i++) {
      if (i !== col) {
        if (!newRow[i].includes(newState[i])) {
          newState[i] = newRow[i][0];
        }
      }
    }

    // Ensure that the new state has a value for each column.
    for (let i = 0; i < newRow.length; i++) {
      if (!newState[i]) {
        newState[i] = newRow[i][0];
      }
    }

    setStateParts(newState.slice());
  };

  const handleSave = () => {
    onUpdate(ser(stateParts));
    setOpen(false);
  };

  const handleCancel = () => {
    setStateParts(des(current));
    setOpen(false);
  };

  const currentStateDisplay = current ? (
    <ul className="list-none p-0 m-0 flex gap-1 flex-col items-start">
      {stateParts.map((part, i) => (
        <li key={i} className="">
          <code className="px-1 py-0.5 bg-muted rounded-md">{part}</code>
        </li>
      ))}
    </ul>
  ) : (
    <span className="text-muted-foreground">[none]</span>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="link"
          size="sm"
          disabled={!!error || loading}
          className="justify-between mx-0 px-0"
        >
          <Edit className="m-0 h-3 w-3" />
          <span className="truncate">{currentStateDisplay}</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-secondary">Edit FSM State</DialogTitle>
          <DialogDescription>
            <p className="text-sm my-2 text-muted-foreground">
              Use this form to update the client's FSM state.
            </p>
            <p className="text-sm my-2 text-muted-foreground">
              Generally states are maintained automatically by the system, but
              occasionally there is an issue that needs to be corrected
              manually.
            </p>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {combos.map((opts, i) => (
            <div
              key={i}
              className="space-y-2 flex justify-between items-center"
            >
              <label className="text-sm font-medium">State {i + 1}</label>
              <Select
                value={stateParts[i] || opts[0]}
                disabled={!!error || loading}
                onValueChange={(value: string) => update(states, value, i)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {opts.map((opt) => (
                    <SelectItem key={opt} value={opt}>
                      {opt}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
        <Separator />
        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={ser(stateParts) === current || loading}
          >
            {loading ? (
              <>
                <Spinner className="mr-2 h-4 w-4" />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
